import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Copy, Trash2, Eraser, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';
import { useSEO } from '../hooks/useSEO';

const RemoveExtraSpaces: React.FC = () => {
  const [text, setText] = useState('');
  const [options, setOptions] = useState({
    removeMultipleSpaces: true,
    removeLeadingSpaces: true,
    removeTrailingSpaces: true,
    removeEmptyLines: true,
    removeAllSpaces: false
  });

  // SEO optimization
  useSEO({
    title: 'Remove Extra Spaces - Clean Up Text Formatting | SnapTools',
    description: 'Free tool to remove extra spaces from text. Clean up multiple spaces, leading/trailing spaces, and empty lines. Perfect for text formatting.',
    keywords: 'remove extra spaces, text cleaner, remove multiple spaces, clean text, format text, remove empty lines, text formatter'
  });

  const processText = () => {
    if (!text) return '';
    
    let result = text;
    
    if (options.removeAllSpaces) {
      return result.replace(/\s/g, '');
    }
    
    if (options.removeMultipleSpaces) {
      result = result.replace(/[ ]+/g, ' ');
    }
    
    if (options.removeLeadingSpaces || options.removeTrailingSpaces) {
      const lines = result.split('\n');
      result = lines.map(line => {
        if (options.removeLeadingSpaces && options.removeTrailingSpaces) {
          return line.trim();
        } else if (options.removeLeadingSpaces) {
          return line.replace(/^\s+/, '');
        } else if (options.removeTrailingSpaces) {
          return line.replace(/\s+$/, '');
        }
        return line;
      }).join('\n');
    }
    
    if (options.removeEmptyLines) {
      result = result.replace(/\n\s*\n/g, '\n').replace(/^\n+|\n+$/g, '');
    }
    
    return result;
  };

  const processedText = processText();

  const copyToClipboard = () => {
    navigator.clipboard.writeText(processedText).then(() => {
      toast.success('Processed text copied to clipboard');
    });
  };

  const clearText = () => {
    setText('');
    toast.success('Text cleared successfully');
  };

  const toggleOption = (key: keyof typeof options) => {
    setOptions(prev => ({
      ...prev,
      [key]: !prev[key],
      // If "remove all spaces" is selected, uncheck others
      ...(key === 'removeAllSpaces' && !prev[key] ? {
        removeMultipleSpaces: false,
        removeLeadingSpaces: false,
        removeTrailingSpaces: false,
        removeEmptyLines: false
      } : {}),
      // If any other option is selected, uncheck "remove all spaces"
      ...(key !== 'removeAllSpaces' && !prev[key] ? {
        removeAllSpaces: false
      } : {})
    }));
  };

  const getStats = () => {
    const originalSpaces = (text.match(/\s/g) || []).length;
    const processedSpaces = (processedText.match(/\s/g) || []).length;
    return {
      originalLength: text.length,
      processedLength: processedText.length,
      spacesRemoved: originalSpaces - processedSpaces,
      originalLines: text ? text.split('\n').length : 0,
      processedLines: processedText ? processedText.split('\n').length : 0
    };
  };

  const stats = getStats();

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-4 lg:p-6">
      <div className="mb-6 sm:mb-8 text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4 px-2">Remove Extra Spaces</h1>
        <p className="text-sm sm:text-base text-gray-600 px-4">Clean up text by removing unwanted spaces and empty lines</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="bg-white rounded-lg sm:rounded-xl shadow-lg">
          <div className="p-4 sm:p-6">
            <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Enter Your Text</h3>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste your text with extra spaces here..."
              className="w-full h-48 sm:h-64 p-3 sm:p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm sm:text-base"
            />
            <div className="mt-4 flex gap-2">
              <button 
                onClick={clearText}
                disabled={!text}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 transition-colors"
              >
                Clear Text
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <h3 className="text-lg font-semibold mb-4">Options</h3>
            <div className="space-y-4">
              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.removeMultipleSpaces}
                  onChange={() => toggleOption('removeMultipleSpaces')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Remove Multiple Spaces</span>
                  <p className="text-xs text-gray-500">Replace multiple consecutive spaces with single space</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.removeLeadingSpaces}
                  onChange={() => toggleOption('removeLeadingSpaces')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Remove Leading Spaces</span>
                  <p className="text-xs text-gray-500">Remove spaces at the beginning of each line</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.removeTrailingSpaces}
                  onChange={() => toggleOption('removeTrailingSpaces')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Remove Trailing Spaces</span>
                  <p className="text-xs text-gray-500">Remove spaces at the end of each line</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.removeEmptyLines}
                  onChange={() => toggleOption('removeEmptyLines')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-700">Remove Empty Lines</span>
                  <p className="text-xs text-gray-500">Remove blank lines and lines with only spaces</p>
                </div>
              </label>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={options.removeAllSpaces}
                  onChange={() => toggleOption('removeAllSpaces')}
                  className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-red-700">Remove All Spaces</span>
                  <p className="text-xs text-red-500">Remove all spaces, tabs, and line breaks</p>
                </div>
              </label>
            </div>

            {text && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Statistics</h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>Characters: {stats.originalLength} → {stats.processedLength}</p>
                  <p>Lines: {stats.originalLines} → {stats.processedLines}</p>
                  <p>Spaces removed: {stats.spacesRemoved}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Processed Text</h3>
              <button
                onClick={copyToClipboard}
                disabled={!processedText}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-300 transition-colors"
              >
                Copy Result
              </button>
            </div>
            <textarea
              value={processedText}
              readOnly
              className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50"
              placeholder="Processed text will appear here..."
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveExtraSpaces;
